/**
 * useMatrixFormState - Matrix-specific state management
 *
 * Manages matrix items, global settings, and apply-to-all logic.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { BatchMode, MatrixItem } from '@/lib/api/batch';
import { createMatrixItem, loadProductMatrixItems, DEFAULT_GLOBAL_SETTINGS } from './matrix-utils';
import type { GlobalMatrixSettings } from './matrix-utils';

interface UseMatrixFormStateOptions {
  initialProductIds?: string[];
  initialProductNames?: Record<string, string>;
  templates: Array<{ id: string; name: string }>;
}

export function useMatrixFormState({
  initialProductIds,
  initialProductNames,
  templates,
}: UseMatrixFormStateOptions) {
  const [matrixItems, setMatrixItems] = useState<MatrixItem[]>([]);
  const [galleryRowId, setGalleryRowId] = useState<string | null>(null);
  const [globalMatrixSettings, setGlobalMatrixSettings] = useState<GlobalMatrixSettings>(
    DEFAULT_GLOBAL_SETTINGS,
  );
  const [showApplyConfirmation, setShowApplyConfirmation] = useState(false);
  const [collectionCountCache, setCollectionCountCache] = useState<Map<string, number>>(new Map());

  // Initialize from product IDs
  const productsInitRef = useRef(false);
  useEffect(() => {
    if (!initialProductIds?.length || productsInitRef.current) return;
    productsInitRef.current = true;
    loadProductMatrixItems(initialProductIds, initialProductNames).then((items) => {
      if (items.length > 0) setMatrixItems(items);
    });
  }, [initialProductIds, initialProductNames]);

  // Initialize matrix mode with one empty row
  const initMatrixIfEmpty = useCallback(() => {
    if (matrixItems.length === 0) {
      setMatrixItems([createMatrixItem(templates[0]?.id, globalMatrixSettings)]);
    }
  }, [matrixItems.length, templates, globalMatrixSettings]);

  // Apply global settings to all rows
  const applyGlobalToAll = useCallback(() => {
    const mode = globalMatrixSettings.mode;
    setMatrixItems((prev) =>
      prev.map((item) => ({
        ...item,
        mode,
        template_id: mode === 'template' ? globalMatrixSettings.templateId : item.template_id,
        collection_id: mode === 'collection' ? globalMatrixSettings.collectionId : item.collection_id,
        custom_prompt: mode === 'prompt' ? globalMatrixSettings.customPrompt : item.custom_prompt,
        aspect_ratios: mode === 'collection' ? ['1:1'] : globalMatrixSettings.aspectRatios,
        variants: mode === 'collection' ? 1 : globalMatrixSettings.variants,
        seed: globalMatrixSettings.seedMode === 'auto' ? null : 12345,
      })),
    );
    setShowApplyConfirmation(false);
  }, [globalMatrixSettings]);

  // Handle global settings change
  const handleGlobalSettingsChange = useCallback(
    (key: string, value: string | number | boolean | string[]) => {
      setGlobalMatrixSettings((prev) => ({ ...prev, [key]: value }));
      if (matrixItems.length > 0) setShowApplyConfirmation(true);
    },
    [matrixItems.length],
  );

  // Open gallery for a specific matrix row
  const handleOpenGalleryForRow = useCallback((rowId: string) => {
    setGalleryRowId(rowId);
  }, []);

  // Handle image selection from gallery (for matrix row)
  const handleGalleryImageSelect = useCallback(
    (images: Array<{ id: string; thumbnail_url: string | null }>) => {
      if (galleryRowId && images.length > 0) {
        const image = images[0];
        setMatrixItems((prev) =>
          prev.map((item) =>
            item.id === galleryRowId
              ? {
                  ...item,
                  input_type: 'workspace' as const,
                  image_id: image.id,
                  _thumbnailUrl: image.thumbnail_url || undefined,
                  _file: undefined,
                }
              : item,
          ),
        );
      }
      setGalleryRowId(null);
    },
    [galleryRowId],
  );

  // Duplicate a row
  const handleDuplicateRow = useCallback((item: MatrixItem) => {
    setMatrixItems((prev) => {
      const index = prev.findIndex((i) => i.id === item.id);
      const duplicated = {
        ...item,
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      };
      const newItems = [...prev];
      newItems.splice(index + 1, 0, duplicated);
      return newItems;
    });
  }, []);

  // Cache collection image counts
  const cacheCollectionCount = useCallback((collectionId: string, count: number) => {
    setCollectionCountCache((prev) => {
      const next = new Map(prev);
      next.set(collectionId, count);
      return next;
    });
  }, []);

  // Get total images for a matrix item (mode-aware)
  const getItemGenerations = useCallback(
    (item: MatrixItem): number => {
      const mode: BatchMode = item.mode || 'template';
      if (mode === 'collection' && item.collection_id) {
        return collectionCountCache.get(item.collection_id) || 1;
      }
      return (item.aspect_ratios?.length || 1) * (item.variants || 1);
    },
    [collectionCountCache],
  );

  return {
    matrixItems,
    setMatrixItems,
    galleryRowId,
    setGalleryRowId,
    globalMatrixSettings,
    setGlobalMatrixSettings,
    handleGlobalSettingsChange,
    applyGlobalToAll,
    showApplyConfirmation,
    setShowApplyConfirmation,
    initMatrixIfEmpty,
    openGalleryForRow: handleOpenGalleryForRow,
    galleryImageSelect: handleGalleryImageSelect,
    duplicateRow: handleDuplicateRow,
    collectionCountCache,
    cacheCollectionCount,
    getItemGenerations,
  };
}
